import React from 'react';
import { cn } from '@/lib/utils';
import {
  getVideoStatusConfig,
  type VideoStatusV10,
} from '@/lib/video-status-v10';

type Variant = 'pill' | 'icon-only' | 'icon-text';
type Size = 'xs' | 'sm' | 'md';

interface StatusBadgeV10Props {
  status: string;
  variant?: Variant;
  size?: Size;
  expanded?: boolean;
  className?: string;
}

const SIZE_ICON: Record<Size, string> = {
  xs: 'w-3 h-3',
  sm: 'w-3.5 h-3.5',
  md: 'w-4 h-4',
};

const SIZE_TEXT: Record<Size, string> = {
  xs: 'text-[10px]',
  sm: 'text-[11px]',
  md: 'text-xs',
};

const SIZE_PAD: Record<Size, string> = {
  xs: 'px-1.5 py-0.5 gap-1',
  sm: 'px-2 py-0.5 gap-1.5',
  md: 'px-2.5 py-1 gap-1.5',
};

export const StatusBadgeV10: React.FC<StatusBadgeV10Props> = ({
  status,
  variant = 'pill',
  size = 'sm',
  expanded = false,
  className,
}) => {
  const config = getVideoStatusConfig(status);
  const Icon = config.icon;

  const alwaysExpanded =
    status === 'to_validate' || status === 'in_discussion';
  const showText =
    variant === 'pill' ||
    (variant === 'icon-text' && (expanded || alwaysExpanded));

  if (variant === 'icon-only') {
    return (
      <span
        className={cn(
          'inline-flex items-center justify-center rounded-full',
          config.pillClassName,
          size === 'xs' ? 'w-5 h-5' : size === 'sm' ? 'w-6 h-6' : 'w-7 h-7',
          className,
        )}
      >
        <Icon className={SIZE_ICON[size]} />
      </span>
    );
  }

  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full font-medium whitespace-nowrap transition-all duration-200 ease-out',
        config.pillClassName,
        SIZE_PAD[size],
        className,
      )}
    >
      <Icon className={cn(SIZE_ICON[size], 'shrink-0')} />
      <span
        className={cn(
          SIZE_TEXT[size],
          'overflow-hidden transition-all duration-200 ease-out',
          showText ? 'max-w-[80px] opacity-100' : 'max-w-0 opacity-0',
        )}
      >
        {config.label}
      </span>
    </span>
  );
};
